import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, BarChart3 } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const AdminReports: React.FC = () => {
  const { setCurrentScreen } = useApp();

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  // Mock data for reports
  const totalSales = 250000;
  const salesGrowth = 15;
  const totalOrders = 1200;

  const salesData = [
    { day: 'Mon', amount: 35000 },
    { day: 'Tue', amount: 42000 },
    { day: 'Wed', amount: 38000 },
    { day: 'Thu', amount: 45000 },
    { day: 'Fri', amount: 52000 },
    { day: 'Sat', amount: 48000 },
    { day: 'Sun', amount: 40000 }
  ];

  const maxAmount = Math.max(...salesData.map(d => d.amount));

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <button
          onClick={handleBack}
          className="p-2 text-slate-400 hover:text-white mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-white">Reports</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Total Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Total Sales</h2>
            <span className="text-sm text-slate-400">Last 30 Days</span>
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            ₹{totalSales.toLocaleString()}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">+{salesGrowth}%</span>
            <TrendingUp size={16} className="text-green-400" />
          </div>
        </motion.div>

        {/* Sales Trend Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <h3 className="text-lg font-semibold text-white mb-4">Sales Trend</h3>
          <div className="h-32 flex items-end space-x-2">
            {salesData.map((data, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-teal-500 rounded-t"
                  style={{ height: `${(data.amount / maxAmount) * 100}%` }}
                />
                <span className="text-xs text-slate-400 mt-2">{data.day}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Key Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <div className="text-center">
            <div className="text-4xl font-bold text-white mb-2">{totalOrders.toLocaleString()}</div>
            <p className="text-slate-400">Total Orders</p>
          </div>
        </motion.div>

        {/* Product Performance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Product Performance</h3>
            <BarChart3 size={20} className="text-teal-400" />
          </div>
          <div className="text-center">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Top Products</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">UltraTech Cement</span>
                <span className="text-teal-400 text-sm font-medium">45%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">Ambuja Cement</span>
                <span className="text-teal-400 text-sm font-medium">32%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 text-sm">ACC Cement</span>
                <span className="text-teal-400 text-sm font-medium">23%</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Navigation for Reports */}
      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-sm bg-slate-800 border-t border-slate-700">
        <div className="flex justify-around py-2">
          <button className="flex flex-col items-center py-2 px-3 rounded-lg text-purple-400">
            <span className="text-xl mb-1">📊</span>
            <span className="text-xs font-medium">Reports</span>
          </button>
          <button className="flex flex-col items-center py-2 px-3 rounded-lg text-slate-400 hover:text-slate-300">
            <span className="text-xl mb-1">📦</span>
            <span className="text-xs font-medium">Products</span>
          </button>
          <button className="flex flex-col items-center py-2 px-3 rounded-lg text-slate-400 hover:text-slate-300">
            <span className="text-xl mb-1">⚙️</span>
            <span className="text-xs font-medium">Settings</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;
